import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Plus, Search, Download, Edit2, Trash2, AlertCircle, RefreshCw, ExternalLink, Filter } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { api } from '../api/client';
import { StatusBadge, ImportanceBadge, UrgencyBadge, Confirm, EmptyState, toast, MicButton } from './UI';
import TaskModal from './TaskModal';
import { format, isAfter } from 'date-fns';

const TODAY = new Date();

const NEW_TASK_DEFAULTS = {
  title: '', status: 'Offen', owner: '', next_step: '', next_step_due_date: null,
  categories: [], importance: 'Mittel', urgency: 'Diese Woche',
  estimated_duration: null, is_recurring: false, recurring_interval: '', notes: '', links: []
};

export const COLUMN_LABELS = {
  title: 'Titel',
  status: 'Status',
  owner: 'Bei wem',
  next_step: 'Nächster Schritt',
  due_date: 'Fällig',
  importance: 'Wichtig',
  urgency: 'Dringend',
  categories: 'Kategorie',
  scope: 'Ebene',
  parent: 'Parent',
  delegation: 'Delegation',
  actions: 'Aktionen',
};

const DELEGATION_BADGES = {
  mine: null,
  delegate: { label: '🤔 Delegierbar', bg: '#E0804A20', color: 'var(--orange)' },
  delegated: { label: '✓ Delegiert', bg: '#52A87820', color: 'var(--green)' },
};

const SCOPE_LABELS = {
  task: 'Task',
  quarter: 'Quartal',
  month: 'Monat',
  week: 'Woche',
  key_result: 'KR',
};

const SCOPE_COLORS = {
  quarter: { bg: '#9B72CF20', color: 'var(--purple)' },
  month: { bg: '#5B8FD420', color: 'var(--blue)' },
  week: { bg: '#52A87820', color: 'var(--green)' },
  key_result: { bg: '#E0804A20', color: 'var(--orange)' },
};

const SORT_KEYS = {
  title: 'title',
  status: 'status',
  owner: 'owner',
  due_date: 'next_step_due_date',
  importance: 'importance',
  urgency: 'urgency',
  categories: 'categories',
  delegation: 'delegation',
};

const DELEGATION_OPTIONS = ['mine', 'delegate', 'delegated'];
const SCOPE_OPTIONS = ['task', 'quarter', 'month', 'week', 'key_result'];
const SCOPE_FILTER_LABELS = { task: 'Task', quarter: 'Quartal', month: 'Monat', week: 'Woche', key_result: 'Key Result' };
const DELEGATION_FILTER_LABELS = { mine: 'Bei mir', delegate: '🤔 Delegierbar', delegated: '✓ Delegiert' };

const arrayify = (v) => Array.isArray(v) ? v : (typeof v === 'string' && v ? [v] : []);
const EMPTY_FILTERS = { status: [], importance: [], urgency: [], category: [], scope: [], delegation: [] };
const hydrateFilters = (raw) => ({
  status: arrayify(raw?.status),
  importance: arrayify(raw?.importance),
  urgency: arrayify(raw?.urgency),
  category: arrayify(raw?.category),
  scope: arrayify(raw?.scope),
  delegation: arrayify(raw?.delegation),
});

function FilterRow({ label, values, options, onChange, getLabel = v => v }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
      <span style={{ fontSize: 11, color: 'var(--text-3)', minWidth: 80, paddingTop: 4 }}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1 }}>
        {options.map(opt => {
          const active = values.includes(opt);
          return (
            <button key={opt}
              type="button"
              onClick={() => onChange(active ? values.filter(v => v !== opt) : [...values, opt])}
              style={{
                fontSize: 11, padding: '3px 9px', borderRadius: 12,
                border: '1px solid', borderColor: active ? 'var(--accent)' : 'var(--border)',
                background: active ? 'var(--accent-dim)' : 'transparent',
                color: active ? 'var(--accent)' : 'var(--text-2)',
                cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: active ? 500 : 400,
              }}>{getLabel(opt)}</button>
          );
        })}
      </div>
    </div>
  );
}

export const DEFAULT_TASK_COLUMNS = [
  { id: 'title', visible: true },
  { id: 'scope', visible: false },
  { id: 'parent', visible: false },
  { id: 'status', visible: true },
  { id: 'delegation', visible: true },
  { id: 'owner', visible: true },
  { id: 'next_step', visible: true },
  { id: 'due_date', visible: true },
  { id: 'importance', visible: true },
  { id: 'urgency', visible: true },
  { id: 'categories', visible: true },
  { id: 'actions', visible: true },
];

const inlineInput = {
  background: 'var(--bg-3)',
  border: '1px solid var(--accent)',
  borderRadius: 'var(--radius)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  padding: '3px 6px',
  outline: 'none',
  width: '100%',
};

const th = {
  padding: '8px 8px',
  textAlign: 'left',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  color: 'var(--text-2)',
  fontSize: 12,
};

const td = { padding: '8px 8px' };

// Reorder visible columns within the full taskColumns array, preserving hidden columns' positions.
function reorderVisible(taskColumns, draggedId, targetId) {
  const visible = taskColumns.filter(c => c.visible);
  const fromIdx = visible.findIndex(c => c.id === draggedId);
  const toIdx = visible.findIndex(c => c.id === targetId);
  if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return taskColumns;

  const [moved] = visible.splice(fromIdx, 1);
  visible.splice(toIdx, 0, moved);

  const result = [];
  let vi = 0;
  for (const col of taskColumns) {
    if (col.visible) result.push(visible[vi++]);
    else result.push(col);
  }
  return result;
}

export default function TaskTable() {
  const { tasks, refreshTasks, settings, updateSettings, statusValues, categories, importanceValues, urgencyValues, isStale, isDone } = useApp();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(() => hydrateFilters(settings?.task_filters));
  const filtersInitialized = useRef(false);
  const lastSavedFilters = useRef(JSON.stringify(filters));

  // Hydrate filters when settings arrive after initial render
  useEffect(() => {
    if (settings && !filtersInitialized.current) {
      const hydrated = hydrateFilters(settings.task_filters);
      setFilters(hydrated);
      lastSavedFilters.current = JSON.stringify(hydrated);
      filtersInitialized.current = true;
    }
  }, [settings]);

  // Debounced auto-save of filters to settings
  useEffect(() => {
    if (!settings) return;
    const current = JSON.stringify(filters);
    if (current === lastSavedFilters.current) return;
    const timer = setTimeout(() => {
      lastSavedFilters.current = current;
      updateSettings({ task_filters: filters }).catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [filters, settings, updateSettings]);
  const [sortBy, setSortBy] = useState(() => settings?.default_sort?.column || 'created_at');
  const [sortDir, setSortDir] = useState(() => settings?.default_sort?.dir || 'desc');
  const sortInitialized = useRef(false);
  const lastSavedSort = useRef(JSON.stringify({ column: sortBy, dir: sortDir }));

  // Hydrate sort when settings arrive after initial render
  useEffect(() => {
    if (settings && !sortInitialized.current) {
      const ds = settings.default_sort || { column: 'created_at', dir: 'desc' };
      setSortBy(ds.column || 'created_at');
      setSortDir(ds.dir || 'desc');
      lastSavedSort.current = JSON.stringify({ column: ds.column, dir: ds.dir });
      sortInitialized.current = true;
    }
  }, [settings]);

  // Debounced auto-save of sort to settings.default_sort
  useEffect(() => {
    if (!settings) return;
    const current = JSON.stringify({ column: sortBy, dir: sortDir });
    if (current === lastSavedSort.current) return;
    const timer = setTimeout(() => {
      lastSavedSort.current = current;
      updateSettings({ default_sort: { column: sortBy, dir: sortDir } }).catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [sortBy, sortDir, settings, updateSettings]);
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [addingRow, setAddingRow] = useState(false);
  const [newRowTitle, setNewRowTitle] = useState('');
  const [draggedCol, setDraggedCol] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const taskColumns = settings?.task_columns || DEFAULT_TASK_COLUMNS;
  const visibleColumns = useMemo(() => taskColumns.filter(c => c.visible).map(c => c.id), [taskColumns]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const sorted = useMemo(() => {
    let list = tasks.filter(t => {
      if (search && !t.title.toLowerCase().includes(search.toLowerCase()) &&
          !(t.owner || '').toLowerCase().includes(search.toLowerCase())) return false;
      if (filters.status?.length && !filters.status.includes(t.status)) return false;
      if (filters.importance?.length && !filters.importance.includes(t.importance)) return false;
      if (filters.urgency?.length && !filters.urgency.includes(t.urgency)) return false;
      if (filters.category?.length && !filters.category.some(c => t.categories?.includes(c))) return false;
      if (filters.scope?.length && !filters.scope.includes(t.scope || 'task')) return false;
      if (filters.delegation?.length && !filters.delegation.includes(t.delegation || 'mine')) return false;
      return true;
    });
    list.sort((a, b) => {
      let va, vb;
      if (sortBy === 'categories') {
        va = (a.categories?.[0] || '').toLowerCase();
        vb = (b.categories?.[0] || '').toLowerCase();
      } else {
        va = a[sortBy] || '';
        vb = b[sortBy] || '';
      }
      return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
    return list;
  }, [tasks, search, filters, sortBy, sortDir]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const deleteTask = async () => {
    await api.deleteTask(confirmDelete.id);
    setConfirmDelete(null);
    refreshTasks();
  };

  const exportCsv = async () => {
    const blob = await api.exportTasksCsv();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'tasks.csv'; a.click();
  };

  const isOverdue = (t) =>
    t.next_step_due_date && isAfter(TODAY, new Date(t.next_step_due_date)) && !isDone(t);

  const startEdit = (taskId, field, value) => {
    setEditingCell({ taskId, field });
    setEditingValue(value ?? '');
  };

  const commitEdit = async (task, field, value) => {
    setEditingCell(null);
    const normalizedNew = value || null;
    const normalizedOld = task[field] || null;
    if (normalizedNew === normalizedOld) return;
    try {
      await api.updateTask(task.id, { ...task, [field]: normalizedNew });
      refreshTasks();
    } catch (err) {
      toast(err.message, 'error');
    }
  };

  const commitSelect = async (task, field, value) => {
    setEditingCell(null);
    if (value === task[field]) return;
    try {
      await api.updateTask(task.id, { ...task, [field]: value });
      refreshTasks();
    } catch (err) {
      toast(err.message, 'error');
    }
  };

  const saveNewRow = async () => {
    const title = newRowTitle.trim();
    if (!title) { setAddingRow(false); setNewRowTitle(''); return; }
    setNewRowTitle('');
    setAddingRow(false);
    try {
      await api.createTask({ ...NEW_TASK_DEFAULTS, title, status: statusValues[0] || 'Offen' });
      refreshTasks();
      toast('Task erstellt', 'success');
    } catch (err) {
      toast(err.message || 'Task konnte nicht erstellt werden', 'error');
    }
  };

  // Drag-and-drop column reordering
  const onDragStart = (id) => (e) => {
    setDraggedCol(id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== dragOverCol) setDragOverCol(id);
  };
  const onDragLeave = () => setDragOverCol(null);
  const onDrop = (targetId) => async (e) => {
    e.preventDefault();
    setDragOverCol(null);
    if (!draggedCol || draggedCol === targetId) { setDraggedCol(null); return; }
    const newColumns = reorderVisible(taskColumns, draggedCol, targetId);
    setDraggedCol(null);
    try {
      await updateSettings({ task_columns: newColumns });
    } catch (err) {
      toast(err.message || 'Spaltenreihenfolge konnte nicht gespeichert werden', 'error');
    }
  };
  const onDragEnd = () => { setDraggedCol(null); setDragOverCol(null); };

  const renderHeader = (id) => {
    const label = COLUMN_LABELS[id];
    const sortKey = SORT_KEYS[id];
    const isDragged = draggedCol === id;
    const isOver = dragOverCol === id && draggedCol && draggedCol !== id;
    const sortIndicator = sortKey && sortBy === sortKey ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

    return (
      <th
        key={id}
        draggable
        onDragStart={onDragStart(id)}
        onDragOver={onDragOver(id)}
        onDragLeave={onDragLeave}
        onDrop={onDrop(id)}
        onDragEnd={onDragEnd}
        onClick={sortKey ? () => handleSort(sortKey) : undefined}
        style={{
          ...th,
          cursor: sortKey ? 'pointer' : 'move',
          opacity: isDragged ? 0.3 : 1,
          borderLeft: isOver ? '2px solid var(--accent)' : '2px solid transparent',
          userSelect: 'none',
        }}
        title="Spalte ziehen zum Verschieben"
      >
        {id === 'actions' ? '' : label}{sortIndicator}
      </th>
    );
  };

  const textCell = (task, field, extraStyle = {}) => {
    const isEditing = editingCell?.taskId === task.id && editingCell?.field === field;
    if (isEditing) {
      return (
        <input
          autoFocus
          value={editingValue}
          onChange={e => setEditingValue(e.target.value)}
          onBlur={() => commitEdit(task, field, editingValue)}
          onKeyDown={e => {
            if (e.key === 'Enter') commitEdit(task, field, editingValue);
            if (e.key === 'Escape') setEditingCell(null);
          }}
          style={{ ...inlineInput, ...extraStyle }}
        />
      );
    }
    return (
      <span
        onClick={() => startEdit(task.id, field, task[field] || '')}
        style={{ cursor: 'text', display: 'block', minHeight: 18, ...extraStyle }}
      >
        {task[field] || <span style={{ color: 'var(--text-3)' }}>—</span>}
      </span>
    );
  };

  const selectCell = (task, field, options, renderBadge) => {
    const isEditing = editingCell?.taskId === task.id && editingCell?.field === field;
    if (isEditing) {
      return (
        <select
          autoFocus
          value={editingValue}
          onChange={e => commitSelect(task, field, e.target.value)}
          onBlur={() => setEditingCell(null)}
          onKeyDown={e => { if (e.key === 'Escape') setEditingCell(null); }}
          style={{ ...inlineInput, width: 'auto' }}
        >
          {options.map(o => <option key={o}>{o}</option>)}
        </select>
      );
    }
    return (
      <span onClick={() => startEdit(task.id, field, task[field] || '')} style={{ cursor: 'pointer' }}>
        {renderBadge(task[field])}
      </span>
    );
  };

  const dateCell = (task) => {
    const isEditing = editingCell?.taskId === task.id && editingCell?.field === 'next_step_due_date';
    if (isEditing) {
      return (
        <input
          type="date"
          autoFocus
          value={editingValue}
          onChange={e => setEditingValue(e.target.value)}
          onBlur={() => commitEdit(task, 'next_step_due_date', editingValue)}
          onKeyDown={e => {
            if (e.key === 'Enter') commitEdit(task, 'next_step_due_date', editingValue);
            if (e.key === 'Escape') setEditingCell(null);
          }}
          style={{ ...inlineInput, width: 120 }}
        />
      );
    }
    const overdue = isOverdue(task);
    return (
      <span
        onClick={() => startEdit(task.id, 'next_step_due_date', task.next_step_due_date?.split('T')[0] || '')}
        style={{ cursor: 'pointer', display: 'block', whiteSpace: 'nowrap' }}
      >
        {task.next_step_due_date
          ? <span className={overdue ? 'overdue' : ''}>
              {overdue && <AlertCircle size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />}
              {format(new Date(task.next_step_due_date), 'dd.MM.yy')}
            </span>
          : <span style={{ color: 'var(--text-3)' }}>—</span>}
      </span>
    );
  };

  const renderCell = (id, task, stale) => {
    switch (id) {
      case 'title':
        return (
          <td key={id} style={{ ...td, maxWidth: 260 }}>
            <div style={{ fontWeight: 500, color: 'var(--text)' }}>
              {textCell(task, 'title')}
            </div>
            {(stale || task.is_recurring || task.links?.length > 0) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                {stale && <span className="stale-indicator"><AlertCircle size={10} style={{ verticalAlign: 'middle' }} /> Inaktiv</span>}
                {task.is_recurring && <RefreshCw size={10} color="var(--text-3)" />}
                {task.links?.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: 'var(--accent)', fontSize: 11 }}>
                    <ExternalLink size={9} />{l.label || 'Link'}
                  </a>
                ))}
              </div>
            )}
          </td>
        );
      case 'status':
        return <td key={id} style={td}>{selectCell(task, 'status', statusValues, v => <StatusBadge status={v} />)}</td>;
      case 'owner':
        return <td key={id} style={{ ...td, color: 'var(--text-2)' }}>{textCell(task, 'owner')}</td>;
      case 'next_step':
        return (
          <td key={id} style={{ ...td, maxWidth: 200 }}>
            {textCell(task, 'next_step', { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}
          </td>
        );
      case 'due_date':
        return <td key={id} style={td}>{dateCell(task)}</td>;
      case 'importance':
        return <td key={id} style={td}>{selectCell(task, 'importance', importanceValues, v => <ImportanceBadge value={v} />)}</td>;
      case 'urgency':
        return <td key={id} style={td}>{selectCell(task, 'urgency', urgencyValues, v => <UrgencyBadge value={v} />)}</td>;
      case 'categories': {
        const isEditing = editingCell?.taskId === task.id && editingCell?.field === 'categories';
        if (isEditing) {
          const taskCats = new Set(task.categories || []);
          const toggleCategory = async (cat) => {
            const next = taskCats.has(cat)
              ? (task.categories || []).filter(c => c !== cat)
              : [...(task.categories || []), cat];
            try {
              await api.updateTask(task.id, { ...task, categories: next });
              refreshTasks();
            } catch (err) { toast(err.message, 'error'); }
          };
          return (
            <td key={id} style={td}>
              <div style={{ background: 'var(--bg-2)', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', padding: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {categories.map(cat => {
                    const active = taskCats.has(cat);
                    return (
                      <button key={cat} onClick={() => toggleCategory(cat)} style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 10,
                        border: '1px solid', borderColor: active ? 'var(--accent)' : 'var(--border)',
                        background: active ? 'var(--accent-dim)' : 'transparent',
                        color: active ? 'var(--accent)' : 'var(--text-2)',
                        cursor: 'pointer', whiteSpace: 'nowrap',
                      }}>{cat}</button>
                    );
                  })}
                </div>
                <button onClick={() => setEditingCell(null)} className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-end' }}>Fertig</button>
              </div>
            </td>
          );
        }
        return (
          <td key={id} style={td} onClick={() => setEditingCell({ taskId: task.id, field: 'categories' })}>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', cursor: 'pointer', minHeight: 18 }}>
              {(task.categories || []).length > 0 ? (
                (task.categories || []).map(c => (
                  <span key={c} style={{ fontSize: 11, color: 'var(--text)', background: 'var(--accent-dim)', padding: '2px 8px', borderRadius: 10, fontWeight: 500 }}>{c}</span>
                ))
              ) : (
                <span style={{ color: 'var(--text-3)', fontSize: 12 }}>—</span>
              )}
            </div>
          </td>
        );
      }
      case 'scope': {
        const s = task.scope || 'task';
        const colors = SCOPE_COLORS[s];
        if (s === 'task') return <td key={id} style={td}><span style={{ fontSize: 11, color: 'var(--text-3)' }}>—</span></td>;
        return (
          <td key={id} style={td}>
            <span style={{
              fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20,
              background: colors.bg, color: colors.color, whiteSpace: 'nowrap',
            }}>
              {SCOPE_LABELS[s]}
            </span>
          </td>
        );
      }
      case 'delegation':
        return <td key={id} style={td}>{selectCell(task, 'delegation', DELEGATION_OPTIONS, v => {
          const badge = DELEGATION_BADGES[v || 'mine'];
          if (!badge) return <span style={{ color: 'var(--text-3)' }}>—</span>;
          return (
            <span style={{
              fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20,
              background: badge.bg, color: badge.color, whiteSpace: 'nowrap',
            }}>{badge.label}</span>
          );
        })}</td>;
      case 'parent': {
        const parent = task.parent_id ? tasks.find(t => t.id === task.parent_id) : null;
        if (!parent) return <td key={id} style={td}><span style={{ color: 'var(--text-3)' }}>—</span></td>;
        return (
          <td key={id} style={td}>
            <button onClick={() => { setEditTask(parent); setShowModal(true); }} style={{
              background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0,
              fontSize: 12, textAlign: 'left', maxWidth: 180,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {parent.title}
            </button>
          </td>
        );
      }
      case 'actions':
        return (
          <td key={id} style={td}>
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="btn btn-icon btn-ghost btn-sm" onClick={() => { setEditTask(task); setShowModal(true); }} title="Bearbeiten">
                <Edit2 size={13} />
              </button>
              <button className="btn btn-icon btn-danger btn-sm" onClick={() => setConfirmDelete(task)} title="Löschen">
                <Trash2 size={13} />
              </button>
            </div>
          </td>
        );
      default:
        return null;
    }
  };

  const colCount = visibleColumns.length;

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 28, flex: 1 }}>Tasks</h1>
        <span style={{ color: 'var(--text-3)', fontSize: 13 }}>{sorted.length} von {tasks.length}</span>
        <button className="btn btn-ghost btn-sm" onClick={exportCsv}><Download size={13} /> CSV</button>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowFilters(f => !f)}>
          <Filter size={13} /> Filter{(() => {
            const n = (filters.status?.length || 0) + (filters.importance?.length || 0) + (filters.urgency?.length || 0) + (filters.category?.length || 0) + (filters.scope?.length || 0) + (filters.delegation?.length || 0);
            return n > 0 ? ` (${n})` : '';
          })()}
        </button>
        <button className="btn btn-primary" onClick={() => { setEditTask(null); setShowModal(true); }}><Plus size={14} /> Neuer Task</button>
      </div>

      {/* Search + Filters */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen..." style={{ paddingLeft: 32 }} />
        </div>
        {showFilters && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 12px', background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
            <FilterRow label="Status" values={filters.status} options={statusValues} onChange={v => setFilter('status', v)} />
            <FilterRow label="Wichtig" values={filters.importance} options={importanceValues} onChange={v => setFilter('importance', v)} />
            <FilterRow label="Dringend" values={filters.urgency} options={urgencyValues} onChange={v => setFilter('urgency', v)} />
            {categories.length > 0 && (
              <FilterRow label="Kategorie" values={filters.category} options={categories} onChange={v => setFilter('category', v)} />
            )}
            <FilterRow label="Ebene" values={filters.scope} options={SCOPE_OPTIONS} onChange={v => setFilter('scope', v)} getLabel={v => SCOPE_FILTER_LABELS[v]} />
            <FilterRow label="Delegation" values={filters.delegation} options={DELEGATION_OPTIONS} onChange={v => setFilter('delegation', v)} getLabel={v => DELEGATION_FILTER_LABELS[v]} />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setFilters({ ...EMPTY_FILTERS })}>
                Alle Filter zurücksetzen
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      {sorted.length === 0 && !addingRow ? (
        <EmptyState icon="📋" title="Keine Tasks gefunden"
          subtitle="Erstell deinen ersten Task oder passe die Filter an."
          action={<button className="btn btn-primary btn-sm" onClick={() => setAddingRow(true)}><Plus size={13} /> Neuer Task</button>} />
      ) : (
        <div style={{ overflowX: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {visibleColumns.map(id => renderHeader(id))}
              </tr>
            </thead>
            <tbody>
              {sorted.map(task => {
                const stale = isStale(task);
                return (
                  <tr key={task.id}
                    className={stale ? 'stale' : ''}
                    style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    {visibleColumns.map(id => renderCell(id, task, stale))}
                  </tr>
                );
              })}

              {/* Inline new row */}
              {addingRow ? (
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-2)' }}>
                  <td colSpan={colCount} style={td}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        autoFocus
                        value={newRowTitle}
                        onChange={e => setNewRowTitle(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveNewRow();
                          if (e.key === 'Escape') { setAddingRow(false); setNewRowTitle(''); }
                        }}
                        placeholder="Titel eingeben oder diktieren… Enter zum Speichern, Escape zum Abbrechen"
                        style={{ ...inlineInput, fontWeight: 500 }}
                      />
                      <MicButton onTranscript={(text) => setNewRowTitle(prev => prev ? `${prev} ${text}` : text)} />
                    </div>
                  </td>
                </tr>
              ) : (
                <tr
                  onClick={() => setAddingRow(true)}
                  style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td colSpan={colCount} style={{ ...td, color: 'var(--text-3)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                      <Plus size={12} /> Neuer Task
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && <TaskModal task={editTask} onClose={() => setShowModal(false)} onSaved={refreshTasks} onNavigate={(t) => setEditTask(t)} />}
      {confirmDelete && <Confirm message={`Task "${confirmDelete.title}" wirklich löschen?`} onConfirm={deleteTask} onCancel={() => setConfirmDelete(null)} />}
    </div>
  );
}
